import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, CompanyDTO, CompanyFullDTO, PlayerHardwareDTO, ResolutionDTO, ScreenHardwareDTO } from 'src/app/services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from '@ngx-seatback/utils';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from '../../../services/util.service';
import { MsOptions } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-screen-hardware-edit',
  templateUrl: './screen-hardware-edit.component.html',
  styleUrls: ['./screen-hardware-edit.component.scss'],
  standalone: false,
})
export class ScreenHardwareEditComponent implements OnInit {
  loading: boolean = true;
  lastRequest: number;
  screenHardware: ScreenHardwareDTO;
  screenHardwareID: number;
  aspectRatio: string;

  manufacturers: CompanyDTO[];
  playerHardwareList: PlayerHardwareDTO[];

  msManufacturersOptions: MsOptions;
  msPlayerHardwareOptions: MsOptions;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    Diagonal: new UntypedFormControl('', []),
    Width: new UntypedFormControl('', []),
    Height: new UntypedFormControl('', []),
    DisplayWidth: new UntypedFormControl('', []),
    DisplayHeight: new UntypedFormControl('', []),
    Description: new UntypedFormControl('', []),
  });

  get Name() {
    return this.form.get('Name');
  }

  get Diagonal() {
    return this.form.get('Diagonal');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Width() {
    return this.form.get('Width');
  }

  get Height() {
    return this.form.get('Height');
  }

  get DisplayWidth() {
    return this.form.get('DisplayWidth');
  }

  get DisplayHeight() {
    return this.form.get('DisplayHeight');
  }

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    private navService: NavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.lastRequest = -1;
    this.screenHardwareID = undefined;
    this.aspectRatio = undefined;
  }

  ngOnInit(): void {
    // read screen hardware id from params
    this.activatedRoute.params.subscribe(params => {
      this.screenHardwareID = +params.screenHardwareID;
      this.refreshFromServer();
    });

    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.screenHardware.name = value;
    });

    this.Diagonal.valueChanges.subscribe(value => {
      this.screenHardware.diagonal = value;
    });

    this.Description.valueChanges.subscribe(value => {
      this.screenHardware.description = value;
    });

    this.Width.valueChanges.subscribe(value => {
      this.screenHardware.width = value;
    });

    this.Height.valueChanges.subscribe(value => {
      this.screenHardware.height = value;
    });

    this.DisplayWidth.valueChanges.subscribe(value => {
      this.screenHardware.nativeResolution.width = value;
    });

    this.DisplayHeight.valueChanges.subscribe(value => {
      this.screenHardware.nativeResolution.height = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.screenHardware.name);
    this.Name.markAsTouched();

    this.Diagonal.setValue(this.screenHardware.diagonal);
    this.Diagonal.markAsTouched();

    this.Description.setValue(this.screenHardware.description);
    this.Description.markAsTouched();

    this.Width.setValue(this.screenHardware.width);
    this.Height.setValue(this.screenHardware.height);
    this.Width.markAsTouched();
    this.Height.markAsTouched();

    this.DisplayWidth.setValue(this.screenHardware.nativeResolution.width);
    this.DisplayHeight.setValue(this.screenHardware.nativeResolution.height);
    this.DisplayWidth.markAsTouched();
    this.DisplayHeight.markAsTouched();
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.screenHardwareID) {
      this.screenHardware = new ScreenHardwareDTO();
      this.screenHardware.nativeResolution = new ResolutionDTO();
      this.loading = false;
    } else {
      this.api
        .ca_md_ScreenHardware_retrieveById(this.screenHardwareID)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (apiScreenHardware: ScreenHardwareDTO) => {
            this.screenHardware = apiScreenHardware;
            if (!this.screenHardware.nativeResolution) {
              this.screenHardware.nativeResolution = new ResolutionDTO();
            }
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateScreenHardware() {
    this.loading = true;
    if (!this.screenHardwareID) {
      if (this.screenHardware.name) {
        this.api
          .ca_md_ScreenHardware_createScreenHardware(this.screenHardware)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: () => {
              this.navService.navigateToPoint(this.util.RoutingID.screenHardware);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .ca_md_ScreenHardware_editScreenHardware(this.screenHardware)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.navService.navigateToPoint(this.util.RoutingID.screenHardware);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  update($event: any, type: string) {
    switch (type) {
      case 'COMPANIES':
        this.screenHardware.manufacturer = null;
        if ($event?.length > 0) {
          this.screenHardware.manufacturer = $event[0];
        }
        break;

      case 'PLAYER_HARDWARE':
        this.screenHardware.mountedTo = null;
        if ($event?.length > 0) {
          this.screenHardware.mountedTo = $event[0];
        }
        break;
    }
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'COMPANIES':
        if (options) {
          this.msManufacturersOptions = options;
        }
        if (this.msManufacturersOptions?.pagination) {
          this.api
            .shared_Company_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msManufacturersOptions?.search),
              },
              this.msManufacturersOptions?.pagination
            )
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe((c: CompanyFullDTO[]) => {
              this.manufacturers = c;
            });
        }
        break;

      case 'PLAYER_HARDWARE':
        if (options) {
          this.msPlayerHardwareOptions = options;
        }
        if (this.msPlayerHardwareOptions?.pagination) {
          this.api
            .ca_md_PlayerHardware_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msPlayerHardwareOptions?.search),
              },
              this.msPlayerHardwareOptions?.pagination
            )
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe((p: PlayerHardwareDTO[]) => {
              this.playerHardwareList = p;
            });
        }
    }
  }

  calculateAspectRatio() {
    this.aspectRatio = undefined;
    if (this.screenHardware?.nativeResolution) {
      const width = Math.ceil(+this.screenHardware.nativeResolution.width);
      const height = Math.ceil(+this.screenHardware.nativeResolution.height);

      this.aspectRatio = this.util.calculateAspectRatio(width, height);

      // set errors for form inputs
      if (width > 0 && height <= 0) {
        this.DisplayHeight.setErrors({ error: true });
      } else {
        this.DisplayHeight.setErrors(null);
      }
      if (width <= 0 && height > 0) {
        this.DisplayWidth.setErrors({ error: true });
      } else {
        this.DisplayWidth.setErrors(null);
      }
      this.DisplayWidth.markAsTouched();
      this.DisplayHeight.markAsTouched();
    }
  }

  checkWidthAndHeightInputs() {
    const width: number = +this.screenHardware.width;
    const height: number = +this.screenHardware.height;

    if (width > 0 && height <= 0) {
      this.Height.setErrors({ error: true });
    } else {
      this.Height.setErrors(null);
    }
    if (width <= 0 && height > 0) {
      this.Width.setErrors({ error: true });
    } else {
      this.Width.setErrors(null);
    }
    this.Height.markAsTouched();
    this.Width.markAsTouched();
  }
}
